/**
 * Place lookups for the map — now served by trips-server (SEC-1).
 *
 * This file used to drive the Maps JS SDK: `PlacesService`, `Place.searchByText`,
 * plus a loader that waited for the places library. All of it ran in the browser
 * with the key from the bundle, which forced that key to have Places enabled —
 * a public licence to spend our Places quota, on the very same key the servers
 * used. The lookups moved to the server, which holds a key nobody else can read;
 * the browser key is now restricted to drawing maps.
 *
 * THE RETURN SHAPE IS UNCHANGED, on purpose. Callers still receive the legacy
 * SDK-flavoured `PlaceDetails` — `photos[].getUrl()`, `geometry.location.lat()`,
 * snake_case fields — so this is a transport swap, not a UI refactor. The
 * adapter below is where the two worlds meet, and it is the only place that
 * knows the server's shape.
 */

import { PlaceDetails, priceLevelLabels } from './placeDetails';
import {
  ServerPlace,
  fetchPlaceFromServer,
  searchPlacesOnServer,
} from '../../../services/places/placesApi';

export interface LocationRecommendation {
  name: string;
  place_id: string;
}

export const normalizePlaceId = (placeId?: string | null): string => {
  if (!placeId) return '';
  const trimmed = String(placeId).trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('places/')) return trimmed.slice(7);
  return trimmed;
};

export const normalizeLocationRecommendations = (
  recommendations?: Array<Record<string, unknown>> | null,
): LocationRecommendation[] => {
  if (!Array.isArray(recommendations)) return [];

  return recommendations
    .map((item) => ({
      name: String(item.name || item.title || '').trim(),
      place_id: normalizePlaceId(
        (item.place_id || item.placeId || item.google_place_id) as string | undefined,
      ),
    }))
    .filter((item) => item.name);
};

const looksLikeGooglePlaceId = (placeId: string) =>
  /^[A-Za-z0-9_-]{10,}$/.test(placeId) && !/\s/.test(placeId);

/**
 * Kept so the eleven call sites that gate on it do not have to change, and
 * because a places library is exactly what is no longer needed: there is nothing
 * to wait for. The map's own loader still governs whether a MAP can be drawn.
 */
export const ensurePlacesLibraryLoaded = async (): Promise<boolean> => true;

/** The server's place → the SDK-shaped object every caller already renders. */
const toLegacyPlaceDetails = (place: ServerPlace): PlaceDetails => {
  const priceLevel = place.priceLabel ? priceLevelLabels.indexOf(place.priceLabel) : -1;

  return {
    name: place.name,
    formatted_address: place.formattedAddress,
    rating: place.rating,
    user_ratings_total: place.userRatingCount,
    formatted_phone_number: place.phone,
    website: place.website,
    url: place.mapsUri,
    price_level: priceLevel >= 0 ? priceLevel : undefined,
    types: place.types || [],
    business_status: place.businessStatus,
    editorial_summary: place.summary ? { overview: place.summary } : undefined,
    opening_hours:
      place.openNow !== undefined || place.openingHours
        ? { open_now: place.openNow, weekday_text: place.openingHours }
        : undefined,
    // getUrl() is what the overlay calls. The link is already resolved, at one
    // width, so the requested size is ignored rather than faked — Google mints a
    // CDN link for the width it was asked for, and there is no resizing it here.
    photos: (place.photos || [])
      .filter((photo) => photo.url)
      .map((photo) => ({ getUrl: () => photo.url as string })),
    geometry: place.location
      ? { location: { lat: () => place.location!.lat, lng: () => place.location!.lng } }
      : undefined,
  };
};

export interface PlaceSearchResult {
  id: string;
  name: string;
  address?: string;
  rating?: number;
  // Coordinates, when Google returns them — lets the picker drop the result as a
  // tappable pin on the day map so nearby options are visible at a glance.
  location?: { lat: number; lng: number };
}

/**
 * Text search for the add-activity place picker (REQ-138). `contextText` (trip
 * destinations) biases ambiguous queries toward the trip's area, and
 * `locationBias` (usually the map's current centre) pulls results toward what
 * the user is looking at so the map fills with nearby places to choose from.
 */
export const searchPlacesByText = async (
  query: string,
  contextText?: string,
  locationBias?: { lat: number; lng: number },
): Promise<PlaceSearchResult[]> => {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const places = await searchPlacesOnServer(contextText ? `${trimmed}, ${contextText}` : trimmed, {
    limit: 12,
    bias: locationBias,
  });

  return places
    .map((place) => ({
      id: normalizePlaceId(place.placeId),
      name: place.name.trim(),
      address: place.formattedAddress,
      rating: place.rating,
      location: place.location,
    }))
    .filter((item) => item.id && item.name);
};

/**
 * `mapElement` is still accepted and ignored: the legacy PlacesService needed a
 * DOM node to attach to, and the callers still have one to give. Dropping the
 * parameter would touch every call site for no benefit.
 */
export const resolvePlaceIdFromGoogle = async (
  mapElement: HTMLElement | null,
  query: string,
  locationBias?: { lat: number; lng: number },
): Promise<string | null> => {
  const [best] = await searchPlacesOnServer(query, { limit: 1, bias: locationBias });
  return best ? normalizePlaceId(best.placeId) : null;
};

export const fetchPlaceDetailsFromGoogle = async (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<PlaceDetails | null> => {
  const normalizedId = normalizePlaceId(placeId);
  const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';

  // The name rides along so the server can fall back to it: an id can be stale
  // (places get merged and removed), and older stops carry a name where an id
  // belongs.
  const query = options?.queryFallback?.trim() || (!candidateId ? normalizedId : '');
  if (!candidateId && !query) return null;

  const place = await fetchPlaceFromServer(candidateId || undefined, query || undefined);
  return place ? toLegacyPlaceDetails(place) : null;
};

export const getPlaceLocation = (place: PlaceDetails): { lat: number; lng: number } | null => {
  const location = place.geometry?.location;
  if (!location) return null;
  return { lat: location.lat(), lng: location.lng() };
};

/**
 * Session cache: a stop's geometry (place_id → lat/lng) is stable, so resolve it
 * once and reuse across every re-plot rather than asking per render — this is
 * what used to fire dozens of GetPlace calls. Caches the promise (dedupes
 * concurrent callers); a null result is evicted so a later attempt can retry.
 */
const geometryCache = new Map<string, Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null>>();

export const fetchPlaceGeometryFromGoogle = (
  mapElement: HTMLElement | null,
  placeId: string,
  options?: {
    queryFallback?: string;
    locationBias?: { lat: number; lng: number };
  },
): Promise<{ lat: number; lng: number; resolvedPlaceId: string } | null> => {
  // place_id (exact) or the fallback query fully determines the geometry; the map
  // element and location bias don't change the resolved point, so leave them out.
  const cacheKey = `${normalizePlaceId(placeId)}|${options?.queryFallback?.trim() || ''}`;
  const cached = geometryCache.get(cacheKey);
  if (cached) return cached;

  const pending = (async () => {
    const normalizedId = normalizePlaceId(placeId);
    const candidateId = looksLikeGooglePlaceId(normalizedId) ? normalizedId : '';
    const query = options?.queryFallback?.trim() || (!candidateId ? normalizedId : '');
    if (!candidateId && !query) return null;

    // One lookup, not two: the server resolves the id (or the name) and returns
    // the coordinates with it, so geometry no longer costs a second round trip.
    const place = await fetchPlaceFromServer(candidateId || undefined, query || undefined);
    if (!place?.location) return null;
    return { ...place.location, resolvedPlaceId: normalizePlaceId(place.placeId) || candidateId };
  })();

  geometryCache.set(cacheKey, pending);
  pending
    .then((result) => {
      if (!result) geometryCache.delete(cacheKey);
    })
    .catch(() => geometryCache.delete(cacheKey));
  return pending;
};
